import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, RefreshCw } from "lucide-react";
import { AiIcon } from "./AiIcon";
import { useToast } from "@/context/ToastContext";
import { api, unwrapObject } from "@/lib/api";
import { useAiFeature } from "@/lib/ai";
import { cn } from "@/lib/utils";

interface Brief {
  greeting: string;
  items: { ref: string; title: string; why: string; link: string; label: string }[];
  tip: string | null;
  made_at: string;
}

const FOLD_KEY = "myday.brief.folded";

/**
 * The AI's brief at the top of My Day: up to five things to start with and why, each linking to its record. Made on the
 * first visit of the day; it can be refreshed once an hour and folded away (remembered on this device).
 */
export function DailyBriefCard() {
  const available = useAiFeature("DAILY_BRIEF");
  const { error } = useToast();
  const [brief, setBrief] = useState<Brief | null>(null);
  const [busy, setBusy] = useState(false);
  const [folded, setFolded] = useState(() => {
    try {
      return localStorage.getItem(FOLD_KEY) === "1";
    } catch {
      return false;
    }
  });

  const load = async (refresh = false) => {
    setBusy(true);
    try {
      setBrief(unwrapObject<Brief>(await api.get(`/api/Ai/DailyBrief${refresh ? "?refresh=1" : ""}`)));
    } catch (e: any) {
      if (refresh) error("Couldn't refresh the brief", e.message || "Please try again");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (available) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [available]);

  if (!available || (!brief && !busy)) return null;

  const fold = () => {
    setFolded(!folded);
    try {
      localStorage.setItem(FOLD_KEY, folded ? "0" : "1");
    } catch {
      /* not remembered; fine */
    }
  };

  return (
    <section className="mx-4 mt-3 rounded-lg border border-primary/25 bg-accent/40 px-3.5 py-3 md:mx-6" aria-label="AI brief for today">
      <div className="flex items-center justify-between gap-2">
        <h2 className="flex min-w-0 items-center gap-1.5 text-[13px] font-semibold">
          <AiIcon className="size-4" busy={busy} />
          <span className="truncate">{brief ? brief.greeting : "Making your brief for today…"}</span>
        </h2>
        <div className="flex shrink-0 items-center gap-1">
          {brief && (
            <button type="button" title="Make the brief again (once an hour)" aria-label="Refresh brief" disabled={busy} onClick={() => void load(true)} className="rounded p-1 text-muted-foreground hover:text-foreground disabled:opacity-50">
              <RefreshCw className={cn("size-3.5", busy && "animate-spin")} />
            </button>
          )}
          <button type="button" title={folded ? "Show the brief" : "Fold the brief"} aria-label={folded ? "Show the brief" : "Fold the brief"} aria-expanded={!folded} onClick={fold} className="rounded p-1 text-muted-foreground hover:text-foreground">
            <ChevronDown className={cn("size-4 transition-transform", folded && "-rotate-90")} />
          </button>
        </div>
      </div>
      {brief && !folded && (
        <>
          {brief.items.length > 0 && (
            <ol className="mt-2 flex flex-col gap-1.5">
              {brief.items.map((i, n) => (
                <li key={i.ref} className="flex gap-2 text-[13px]">
                  <span className="mt-px font-mono text-xs font-semibold text-accent-foreground">{n + 1}.</span>
                  <span className="min-w-0">
                    <Link to={i.link} className="font-semibold hover:text-primary hover:underline">
                      {i.title}
                    </Link>
                    <span className="text-muted-foreground"> · {i.label}</span>
                    <span className="block text-xs text-muted-foreground">{i.why}</span>
                  </span>
                </li>
              ))}
            </ol>
          )}
          {brief.tip && <p className="mt-2 text-xs text-muted-foreground">Tip: {brief.tip}</p>}
        </>
      )}
    </section>
  );
}
